(() => {
  'use strict';

  function makePlan(cart, config) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const bearQuantity = items.reduce((sum, item) => sum + (item.handle === config.bearHandle ? Number(item.quantity) || 0 : 0), 0);
    const updates = {};
    const additions = [];
    const unavailable = [];
    for (const gift of config.gifts) {
      const eligible = bearQuantity >= gift.threshold;
      const lines = items.filter(item => item.handle === gift.handle || (gift.id && String(item.variant_id) === String(gift.id)));
      let retained = false;
      if (eligible && (!gift.id || !gift.available)) unavailable.push(gift.handle);
      for (const line of lines) {
        const correctVariant = String(line.variant_id) === String(gift.id);
        const desired = eligible && correctVariant && !retained ? 1 : 0;
        if (desired) {
          retained = true;
          if (Number(line.final_price) !== 0) unavailable.push(gift.handle);
        }
        if (Number(line.quantity) !== desired) updates[line.key] = desired;
      }
      if (eligible && !retained && gift.id && gift.available) {
        additions.push({id: gift.id, quantity: 1, properties: {_cp_bundle_gift: 'true'}});
      }
    }
    return {bearQuantity, updates, additions, unavailable, changed: Object.keys(updates).length > 0 || additions.length > 0};
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = {makePlan};
    return;
  }
  const configElement = document.getElementById('cp-bundle-gift-config');
  if (!configElement || window.CuddlePeekBundleGifts) return;
  const config = JSON.parse(configElement.textContent);
  const rawFetch = window.fetch.bind(window);
  const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
  let timer;
  let active;
  let nativeRequests = 0;
  let nativeRevision = 0;
  let lastInteraction = 0;
  let rendering = false;
  let replayingCheckout = false;
  let checkoutPending = false;
  let state = 'idle';
  let needsRender = false;
  const disabledInputs = new WeakMap();
  let errorMessage = '';
  let lastError = null;
  const wait = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));

  function updateStatus() {
    document.documentElement.dataset.cpGiftSync = state;
    const notice = document.getElementById('cp-bundle-gift-notice');
    if (notice) {
      notice.hidden = state !== 'error';
      notice.querySelector('[data-cp-gift-message]').textContent = errorMessage;
      notice.querySelector('[data-cp-gift-retry]').disabled = state === 'busy';
    }
    document.querySelectorAll('[data-cp-gift-sync-status]').forEach(element => {
      element.hidden = state !== 'busy' && state !== 'error';
      element.textContent = state === 'error' ? errorMessage : config.messages.syncing;
    });
    document.querySelectorAll('cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"]').forEach(input => {
      if (state === 'busy') {
        if (!disabledInputs.has(input)) disabledInputs.set(input, input.disabled);
        input.disabled = true;
      } else if (disabledInputs.has(input)) {
        input.disabled = disabledInputs.get(input);
        disabledInputs.delete(input);
      }
    });
    document.querySelectorAll('.cart__checkout-button').forEach(button => {
      if (state === 'busy') button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  }

  async function request(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await rawFetch(path instanceof URL ? path : `${root}${path}`, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
        headers: body ? {'Content-Type': 'application/json', Accept: 'application/json'} : {Accept: 'application/json'},
        ...(body ? {body: JSON.stringify(body)} : {})
      });
      const result = await response.json();
      if (!response.ok || result.errors || (result.status && Number(result.status) >= 400)) {
        const failure = new Error(config.messages.error);
        failure.context = 'cart-request';
        failure.endpoint = path instanceof URL ? path.pathname + path.search : path;
        failure.status = response.status;
        failure.description = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : typeof result.errors === 'string' ? result.errors : '';
        throw failure;
      }
      return result;
    } catch (error) {
      error.context = error.context || 'cart-request';
      error.endpoint = error.endpoint || (path instanceof URL ? path.pathname + path.search : path);
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }

  async function waitForNative() {
    const deadline = Date.now() + 15000;
    while (nativeRequests || Date.now() - lastInteraction < 450) {
      if (Date.now() > deadline) {
        const failure = new Error(config.messages.error);
        failure.context = 'native-cart-timeout';
        failure.description = `Pending native requests: ${nativeRequests}`;
        throw failure;
      }
      await wait(75);
    }
  }

  async function renderCart(cart) {
    const drawer = document.querySelector('cart-drawer');
    const pageItems = document.getElementById('main-cart-items');
    const pageFooter = document.getElementById('main-cart-footer');
    const sections = ['cart-icon-bubble'];
    if (drawer) sections.push('cart-drawer');
    if (pageItems?.dataset.id) sections.push(pageItems.dataset.id);
    if (pageFooter?.dataset.id) sections.push(pageFooter.dataset.id);
    const refreshedCart = await request('cart/update.js', {
      updates: {},
      sections: [...new Set(sections)],
      sections_url: window.location.pathname
    });
    cart = refreshedCart;
    const html = refreshedCart.sections || {};
    if (sections.some(section => typeof html[section] !== 'string')) {
      const failure = new Error(config.messages.error);
      failure.context = 'cart-section-render';
      failure.description = sections.filter(section => typeof html[section] !== 'string').join(',');
      throw failure;
    }
    rendering = true;
    try {
      if (drawer && typeof drawer.renderContents === 'function') {
        drawer.renderContents({...cart, sections: html}, true);
        drawer.classList.toggle('is-empty', cart.item_count === 0);
      } else {
        const bubble = document.getElementById('cart-icon-bubble');
        const parsed = new DOMParser().parseFromString(html['cart-icon-bubble'], 'text/html');
        if (bubble) bubble.innerHTML = parsed.querySelector('.shopify-section')?.innerHTML || '';
      }
      for (const target of [pageItems, pageFooter]) {
        if (!target?.dataset.id) continue;
        const parsed = new DOMParser().parseFromString(html[target.dataset.id], 'text/html');
        const source = parsed.querySelector(`#${target.id} .js-contents`);
        const destination = target.querySelector('.js-contents');
        if (!source || !destination) throw new Error(config.messages.error);
        destination.innerHTML = source.innerHTML;
        target.classList.toggle('is-empty', cart.item_count === 0);
      }
      document.querySelector('cart-items')?.classList.toggle('is-empty', cart.item_count === 0);
      updateStatus();
    } finally {
      rendering = false;
    }
  }

  async function reconcile() {
    for (let pass = 0; pass < 8; pass += 1) {
      await waitForNative();
      const revision = nativeRevision;
      const cart = await request('cart.js');
      if (nativeRequests || revision !== nativeRevision) continue;
      const plan = makePlan(cart, config);
      if (plan.unavailable.length) throw new Error(config.messages.unavailable);
      if (!plan.changed) {
        if (needsRender) {
          await renderCart(cart);
          needsRender = false;
        }
        if (nativeRequests || revision !== nativeRevision) continue;
        document.dispatchEvent(new CustomEvent('cp:gifts-updated', {detail: {cart, bearQuantity: plan.bearQuantity}}));
        return cart;
      }
      if (Object.keys(plan.updates).length) await request('cart/update.js', {updates: plan.updates});
      else await request('cart/add.js', {items: plan.additions});
      needsRender = true;
    }
    throw new Error(config.messages.error);
  }

  function sync() {
    clearTimeout(timer);
    if (active) return active;
    state = 'busy';
    errorMessage = '';
    updateStatus();
    active = reconcile().then(cart => {
      state = 'idle';
      lastError = null;
      document.documentElement.removeAttribute('data-cp-gift-error');
      return cart;
    }).catch(error => {
      state = 'error';
      lastError = {name: error.name, message: error.message, context: error.context || 'reconciliation', endpoint: error.endpoint || '', status: error.status || 0, description: error.description || ''};
      document.documentElement.dataset.cpGiftError = JSON.stringify(lastError);
      console.warn('CuddlePeek gift sync failed', lastError);
      errorMessage = error.message === config.messages.unavailable ? config.messages.unavailable : config.messages.error;
      throw error;
    }).finally(() => {
      active = null;
      updateStatus();
    });
    return active;
  }

  function schedule(delay = 100) {
    if (rendering) return;
    clearTimeout(timer);
    timer = setTimeout(() => sync().catch(() => {}), delay);
  }

  window.fetch = function(input, init) {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href);
    const method = (init?.method || (typeof input === 'object' && input.method) || 'GET').toUpperCase();
    const cartMutation = method !== 'GET' && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
    const cartSectionRead = method === 'GET' && /\/cart\/?$/.test(url.pathname) && (url.searchParams.has('section_id') || url.searchParams.has('sections'));
    if (url.origin !== window.location.origin || (!cartMutation && !cartSectionRead)) return rawFetch(input, init);
    nativeRequests += 1;
    nativeRevision += 1;
    lastInteraction = Date.now();
    return rawFetch(input, init).finally(() => {
      nativeRequests -= 1;
      nativeRevision += 1;
      lastInteraction = Date.now();
      schedule();
    });
  };

  function hookDrawer() {
    const Drawer = customElements.get('cart-drawer');
    if (!Drawer || Drawer.prototype.cpGiftSyncWrapped) return;
    const original = Drawer.prototype.renderContents;
    if (typeof original !== 'function') return;
    Drawer.prototype.renderContents = function(...args) {
      const result = original.apply(this, args);
      updateStatus();
      schedule();
      return result;
    };
    Drawer.prototype.cpGiftSyncWrapped = true;
  }

  async function checkout(action) {
    if (checkoutPending) return;
    checkoutPending = true;
    try {
      await sync();
      replayingCheckout = true;
      action();
    } catch {
      document.getElementById('cp-bundle-gift-notice')?.querySelector('button')?.focus();
    } finally {
      replayingCheckout = false;
      checkoutPending = false;
    }
  }

  document.addEventListener('click', event => {
    const retry = event.target.closest('[data-cp-gift-retry]');
    if (retry) {
      event.preventDefault();
      sync().catch(() => {});
      return;
    }
    const control = event.target.closest('cart-items .quantity__button, cart-drawer-items .quantity__button, cart-items cart-remove-button, cart-drawer-items cart-remove-button');
    if (control && state === 'busy') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const button = event.target.closest('button[name="checkout"], input[name="checkout"]');
    const link = event.target.closest('a[href]');
    let checkoutLink = false;
    if (link) {
      const url = new URL(link.href, window.location.href);
      checkoutLink = url.origin === window.location.origin && /\/checkout\/?$/.test(url.pathname);
    }
    if (replayingCheckout || (!button && !checkoutLink)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || button?.disabled) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const buttonId = button?.id;
    const formId = button?.form?.id;
    const href = checkoutLink ? link.href : null;
    checkout(() => {
      if (href) window.location.assign(href);
      else {
        const current = (buttonId && document.getElementById(buttonId)) || (formId && document.querySelector(`[form="${formId}"][name="checkout"]`)) || button;
        if (current?.isConnected) current.click();
      }
    });
  }, true);

  document.addEventListener('submit', event => {
    if (replayingCheckout || event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const formId = event.target.id;
    const submitterId = event.submitter.id;
    checkout(() => {
      const form = document.getElementById(formId);
      const button = document.getElementById(submitterId);
      if (form && button?.form === form) form.requestSubmit(button);
    });
  }, true);

  document.addEventListener('change', event => {
    if (!event.target.matches('cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"]')) return;
    lastInteraction = Date.now();
    schedule(550);
  }, true);
  document.addEventListener('cartQuantityUpdated', () => schedule());
  document.addEventListener('shopify:section:load', () => {hookDrawer(); schedule();});
  window.addEventListener('pageshow', () => schedule());
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => schedule());
  }
  hookDrawer();
  customElements.whenDefined('cart-drawer').then(hookDrawer);
  window.CuddlePeekBundleGifts = {sync, get state() {return state;}, get lastError() {return lastError;}};
  schedule();
})();
